/* GameBible — the official WildQuest game bible, rendered from the
 * canonical markdown doc at /WildQuest-Rarity-Bible.md. Users open it
 * from the hamburger menu to learn everything about rarity, PvP,
 * credits, tiers, marketplace and more.
 *
 * Design: reader-first — table of contents on the left of the scroll,
 * long-form typography in the body, sticky download pill at the top.
 * Uses `react-markdown` + `remark-gfm` (tables, task lists, autolinks).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, BookOpen, Download, Loader2, Search, X, Play } from 'lucide-react';
import { useI18n } from '../i18n/I18nContext';

const BIBLE_BASE = '/WildQuest-Rarity-Bible';
const BIBLE_LANGS = ['es', 'fr'];  // localized copies produced by translate_game_bible.py
const EXPLAINER_LANGS = ['en', 'es', 'fr'];
const EXPLAINER_POSTER = '/game-explainer-poster.jpg';

/** Pick the best bible URL for the current locale. Falls back to
 *  English if the active locale isn't localised OR if the localized
 *  file 404s at fetch-time (see fetchBibleFor below). */
function pickBibleUrl(lang) {
  const short = String(lang || 'en').toLowerCase().split('-')[0];
  if (BIBLE_LANGS.includes(short)) return `${BIBLE_BASE}.${short}.md`;
  return `${BIBLE_BASE}.md`;
}

/** Try the localized bible first, fall back to English if it's missing.
 *  Returns { md, url } where `url` is what was actually served. */
async function fetchBibleFor(lang) {
  const primary = pickBibleUrl(lang);
  try {
    const r = await fetch(primary);
    if (r.ok) return { md: await r.text(), url: primary };
  } catch { /* fall through */ }
  // Fallback to English base
  const en = `${BIBLE_BASE}.md`;
  const r2 = await fetch(en);
  if (!r2.ok) throw new Error('bible load failed');
  return { md: await r2.text(), url: en };
}

/** Pick the best explainer video for the current i18n language. Falls
 *  back to English if the active locale isn't localised. */
function pickExplainer(lang) {
  const short = String(lang || 'en').toLowerCase().split('-')[0];
  const chosen = EXPLAINER_LANGS.includes(short) ? short : 'en';
  return `/game-explainer-${chosen}.mp4`;
}

/** Strip anything technical / code-shaped from the raw bible markdown
 *  so players never see hex values, JSON schemas, sound hook names,
 *  IDs or fenced code blocks. Reader-friendly output only. */
function stripCode(md) {
  if (!md) return md;
  let out = md;
  // 1. Fenced code blocks (```...```)
  out = out.replace(/```[\s\S]*?```/g, '');
  // 2. Inline code — drop the backtick token entirely
  out = out.replace(/`[^`\n]*`/g, '');
  // 3. Chop the entire "Data Schema Reference" chapter (H2 until next H2).
  out = out.replace(/^##\s+[^\n]*Data Schema Reference[\s\S]*?(?=^##\s+|\Z)/gim, '');
  // 4. Any leftover empty bullets ("- : ") that lose meaning without codes.
  out = out.replace(/^[-*]\s+[^:\n]+:\s*$/gm, '');
  // 5. Collapse 3+ consecutive blank lines to a single blank line.
  out = out.replace(/\n{3,}/g, '\n\n');
  return out;
}

/** Slugify a heading exactly like GitHub / react-markdown does. */
function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

/** Extract section headings (##, ###) so the search bar can jump to them. */
function extractHeadings(md) {
  if (!md) return [];
  const lines = md.split('\n');
  const out = [];
  for (const line of lines) {
    const m = /^(#{1,3})\s+(.+?)\s*$/.exec(line);
    if (!m) continue;
    const level = m[1].length;
    const text = m[2].replace(/[*_`]/g, '').trim();
    if (level > 3) continue;
    out.push({ level, text, id: slugify(text) });
  }
  return out;
}

export default function GameBible() {
  const nav = useNavigate();
  const { lang } = useI18n();
  const [text, setText] = useState(null);
  const [bibleUrl, setBibleUrl] = useState(`${BIBLE_BASE}.md`);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    let dead = false;
    fetchBibleFor(lang)
      .then(({ md, url }) => { if (!dead) { setText(stripCode(md)); setBibleUrl(url); } })
      .catch(() => { if (!dead) setErr('The game bible could not be loaded right now.'); });
    return () => { dead = true; };
  }, [lang]);

  const headings = useMemo(() => extractHeadings(text), [text]);
  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return [];
    return headings.filter((h) => h.text.toLowerCase().includes(q)).slice(0, 8);
  }, [q, headings]);

  // Keyboard: Enter jumps to first match; Escape clears the query.
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setQuery(''); e.currentTarget.blur(); }
    if (e.key === 'Enter' && matches.length) {
      e.preventDefault();
      jumpTo(matches[0].id);
    }
  };

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: 'smooth' });
    // Flash the target briefly so the user's eye lands on it.
    el.classList.add('wq-bible-highlight');
    setTimeout(() => el.classList.remove('wq-bible-highlight'), 1600);
  };

  return (
    <div className="pb-10" data-testid="game-bible-page">
      {/* Header */}
      <button
        type="button"
        onClick={() => nav(-1)}
        data-testid="bible-back"
        className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-white mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shrink-0">
          <BookOpen className="w-5 h-5 text-emerald-300" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300">WildQuest</p>
          <h1 className="font-serif text-3xl leading-tight">Game Bible</h1>
          <p className="text-sm text-muted-foreground mt-1">
            The complete field manual — discovery, battle, ecology, credits, community and every feature in the app.
          </p>
        </div>
      </div>

      <a
        href={bibleUrl}
        download
        data-testid="bible-download"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-[11px] uppercase tracking-widest text-emerald-200 mb-4"
      >
        <Download className="w-3.5 h-3.5" /> Download markdown
      </a>

      {/* Cinematic game explainer — Sora 2 outdoor cinematics stitched
          with live gameplay recordings. Autoplay off, click-to-play. */}
      <ExplainerVideo />

      {/* Search — sticky at the top so a returning player can jump to
          any tier or FAQ without scrolling the 600-line doc. */}
      <div className="sticky top-16 z-30 -mx-1 mb-5" data-testid="bible-search-wrapper">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search the bible — tier, FAQ, PvP…"
            data-testid="bible-search-input"
            className="w-full h-11 pl-10 pr-10 rounded-full bg-[#0f1a15] border border-emerald-400/25 text-sm outline-none focus:border-emerald-400/60 placeholder:text-muted-foreground/60 shadow-lg shadow-black/40"
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); searchRef.current?.focus(); }}
              data-testid="bible-search-clear"
              aria-label="Clear search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-white/5"
            >
              <X className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          )}
          {q && (
            <div
              className="absolute left-0 right-0 mt-2 rounded-2xl bg-[#0f1a15]/98 border border-emerald-400/25 shadow-2xl shadow-black/60 overflow-hidden"
              data-testid="bible-search-results"
            >
              {matches.length === 0 ? (
                <p className="px-4 py-3 text-xs text-muted-foreground">No sections match "{query}".</p>
              ) : matches.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { jumpTo(m.id); setQuery(''); }}
                  data-testid={`bible-search-match-${m.id}`}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-emerald-500/10 border-b border-white/5 last:border-0 flex items-center gap-2"
                >
                  <span className={`text-[10px] uppercase tracking-widest ${m.level === 2 ? 'text-emerald-300' : 'text-muted-foreground'}`}>
                    {m.level === 1 ? 'Book' : m.level === 2 ? 'Chapter' : 'Section'}
                  </span>
                  <span className="truncate">{m.text}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      {err ? (
        <div className="rounded-2xl bg-rose-500/10 border border-rose-400/30 px-4 py-3 text-sm text-rose-200" data-testid="bible-error">
          {err}
        </div>
      ) : !text ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="bible-loading">
          <Loader2 className="w-4 h-4 animate-spin text-accent" /> Loading the bible…
        </div>
      ) : (
        <article
          className="wq-bible-prose text-[15px] leading-relaxed text-white/85 max-w-none"
          data-testid="bible-content"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ node, children, ...props }) => (
                <h1 id={slugify(String(children))} {...props}>{children}</h1>
              ),
              h2: ({ node, children, ...props }) => (
                <h2 id={slugify(String(children))} {...props}>{children}</h2>
              ),
              h3: ({ node, children, ...props }) => (
                <h3 id={slugify(String(children))} {...props}>{children}</h3>
              ),
              // Defensive: even if a stray code node survives stripping,
              // hide it completely from the reader view.
              code: () => null,
              pre: () => null,
            }}
          >
            {text}
          </ReactMarkdown>
        </article>
      )}
    </div>
  );
}

/** Inline video player — poster + tap-to-play, uses native controls for
 *  reliable behaviour across iOS Safari, Chrome and Firefox. Picks the
 *  language-baked caption video that matches the active i18n lang. */
function ExplainerVideo() {
  const { lang } = useI18n();
  const src = pickExplainer(lang);
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  const start = () => {
    setPlaying(true);
    setTimeout(() => { ref.current?.play?.().catch(() => {}); }, 20);
  };
  return (
    <div
      data-testid="bible-explainer"
      className="relative mb-6 rounded-3xl overflow-hidden border border-emerald-400/25 shadow-2xl shadow-black/50 bg-black aspect-video"
    >
      {!playing ? (
        <button
          type="button"
          onClick={start}
          data-testid="bible-explainer-play"
          aria-label="Play game explainer"
          className="group absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${EXPLAINER_POSTER})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <span className="h-16 w-16 rounded-full bg-emerald-500/90 flex items-center justify-center border-2 border-white/30 shadow-xl group-hover:scale-110 transition-transform">
              <Play className="w-7 h-7 text-black ml-1" fill="currentColor" strokeWidth={0} />
            </span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-200">Play the WildQuest explainer</span>
            <span className="font-serif text-2xl leading-tight text-white text-center px-6">
              Get outside. Discover wildlife.
            </span>
            <span className="text-[10px] uppercase tracking-widest text-white/60">1:15 · Cinematic + real gameplay</span>
          </span>
        </button>
      ) : (
        <video
          ref={ref}
          key={src}
          data-testid="bible-explainer-video"
          src={src}
          poster={EXPLAINER_POSTER}
          controls
          playsInline
          preload="metadata"
          className="w-full h-full bg-black"
        />
      )}
    </div>
  );
}
